import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth.context";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  ArrowLeft, ArrowRight, CheckCircle2, CreditCard, DollarSign, Calendar,
  Clock, Loader, AlertTriangle, Info, XCircle, ShieldCheck, Download,
  Printer, FileText, Check, User, BadgeCheck, Phone, Mail, Wallet,
  TrendingUp, ChevronDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { cn, formatCurrency, formatTerm } from "@/lib/utils";
import { motion } from "framer-motion";
import { format } from "date-fns";
import LoanConflictModal, { LoanConflictData } from "@/components/LoanConflictModal";
import { GuarantorFields } from "@/components/loans/shared/GuarantorFields";

type LoanTypeCategory = "recommended" | "eligible" | "not_eligible";

interface ClassifiedLoanType {
  loanType: any;
  category: LoanTypeCategory;
  notEligibleReasons: string[];
  score: number;
}

type Step = 0 | 1 | 2 | 3 | 4;

const stepLabels = ["Select Member", "Loan Details", "Repayment", "Review"];

export default function AdminLoanApply() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [step, setStep] = useState<Step>(0);
  const [submitting, setSubmitting] = useState(false);

  // Member selection state
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [memberPopoverOpen, setMemberPopoverOpen] = useState(false);

  // Form state
  const [amount, setAmount] = useState("");
  const [purpose, setPurpose] = useState("");
  const [loanTypeId, setLoanTypeId] = useState("0");
  const [termMonths, setTermMonths] = useState("12");
  const [declarationChecked, setDeclarationChecked] = useState(false);
  const [guarantors, setGuarantors] = useState<{ id: number; memberId?: number; name: string; memberCode: string; phone: string; email: string; savings: number }[]>([]);
  const printRef = useRef<HTMLDivElement>(null);

  // Conflict modal state
  const [conflictOpen, setConflictOpen] = useState(false);
  const [conflictCode, setConflictCode] = useState<"ACTIVE_LOAN_EXISTS" | "DUPLICATE_APPLICATION">("ACTIVE_LOAN_EXISTS");
  const [conflictData, setConflictData] = useState<LoanConflictData | null>(null);

  const selectedMemberId = selectedMember?.id;

  // ─── Data Fetching ───

  const { data: teachers = [], isLoading: isTeachersLoading } = useQuery(
    ["teachers"],
    async () => {
      const { data } = await api.get("/teachers?limit=1000");
      const results = data.results || data.data || [];
      return results.map((t: any) => ({
        id: t.id,
        label: t.fullName || `${t.firstName || ""} ${t.lastName || ""}`.trim(),
        memberCode: t.memberCode || t.member?.memberCode || "",
        email: t.email || "",
        phone: t.phone || t.phoneNumber || "",
        hasActiveLoan: t.loans?.some((l: any) => ["active", "disbursed"].includes(l.status?.toLowerCase())) || false,
        loanCount: t.loans?.length || 0,
      }));
    }
  );

  const { data: loanTypes, isLoading: loanTypesLoading } = useQuery(
    ["loan-types"],
    async () => {
      const { data } = await api.get("/loan-types");
      const items = Array.isArray(data.data) ? data.data : [];
      return items.filter((lt: any) => lt.isActive !== false);
    },
    { enabled: !!selectedMemberId }
  );

  const { data: groupSettings } = useQuery(
    ["teacherGroup"],
    async () => {
      const { data } = await api.get("/teacher-groups");
      return data.data?.[0];
    }
  );

  const { data: orgSettings } = useQuery(
    ["org-settings"],
    async () => {
      const { data } = await api.get("/settings");
      return data.data || {};
    },
    { staleTime: 300000 }
  );

  const { data: memberContributions, isLoading: contributionsLoading } = useQuery(
    ["admin-member-contributions", selectedMemberId],
    async () => {
      if (!selectedMemberId) return [];
      const { data } = await api.get(`/contributions/teacher/${selectedMemberId}`);
      return data.data || data.results || [];
    },
    { enabled: !!selectedMemberId }
  );

  const { data: memberLoans = [] } = useQuery(
    ["admin-member-loans", selectedMemberId],
    async () => {
      if (!selectedMemberId) return [];
      const { data } = await api.get(`/loans/teacher/${selectedMemberId}`);
      return data.data || data.results || [];
    },
    { enabled: !!selectedMemberId }
  );

  const { data: memberProfile } = useQuery(
    ["admin-member-profile", selectedMemberId],
    async () => {
      if (!selectedMemberId) return null;
      const { data } = await api.get(`/teachers/${selectedMemberId}`);
      return data.data || data;
    },
    { enabled: !!selectedMemberId, staleTime: 60000 }
  );

  // ─── Computed Values ───

  const memberInfo = useMemo(() => {
    if (!selectedMember) return { fullName: "", phone: "", email: "", memberNumber: "" };
    const mp = memberProfile || {};
    const rawCode = selectedMember.memberCode || mp.memberCode || mp.teacherId || "";
    return {
      fullName: selectedMember.label || mp.fullName || mp.fullNames || "Not provided",
      phone: selectedMember.phone || mp.phone || mp.phoneNumber || "Not provided",
      email: selectedMember.email || mp.email || "Not provided",
      memberNumber: rawCode && !String(rawCode).startsWith("TMP-") ? rawCode : "N/A",
    };
  }, [selectedMember, memberProfile]);

  const teacherSavings = useMemo(
    () =>
      (memberContributions || [])
        .filter((c: any) => c.isVerified || c.verified || c.verificationStatus === "Verified" || c.status === "verified")
        .reduce((sum: number, c: any) => sum + (Number(c.amount) || Number(c.depositAmount) || 0), 0),
    [memberContributions]
  );

  // Loan policies from system settings
  const loanPolicies = (orgSettings as any)?.loanPolicies || {};
  const maxLoanMultiplier = Number(loanPolicies.maxLoanMultiplier) || Number(groupSettings?.maxLoanMultiplier) || 3;
  const allowMultipleActiveLoans = loanPolicies.allowMultipleActiveLoans === true;
  const maxActiveLoansPerMember = Number(loanPolicies.maxActiveLoansPerMember) || 1;
  const allowWithOutstanding = loanPolicies.allowLoanWithOutstandingBalance === true;
  const deductOutstanding = loanPolicies.deductOutstandingBalanceFromEligibility !== false;
  const globalMinLoan = Number(loanPolicies.globalMinimumLoanAmount) || 0;
  const globalMaxLoan = Number(loanPolicies.globalMaximumLoanAmount) || Infinity;

  const hasActiveLoan = useMemo(() => {
    const activeLoans = (memberLoans || []).filter((l: any) =>
      ["active", "disbursed"].includes(l.status?.toLowerCase())
    );
    if (allowMultipleActiveLoans) {
      return activeLoans.length >= maxActiveLoansPerMember;
    }
    return activeLoans.length > 0;
  }, [memberLoans, allowMultipleActiveLoans, maxActiveLoansPerMember]);

  const outstandingLoanBalance = useMemo(
    () =>
      (memberLoans || [])
        .filter((l: any) => ["active", "disbursed"].includes(l.status?.toLowerCase()))
        .reduce((sum: number, l: any) => sum + (Number(l.dueAmount) || Number(l.amount) || 0), 0),
    [memberLoans]
  );

  const pendingApplicationCount = useMemo(
    () =>
      (memberLoans || [])
        .filter((l: any) => ["pending", "approved", "pending_approval", "approved_by_leaders", "ready_for_disbursement"].includes(l.status?.toLowerCase())).length,
    [memberLoans]
  );

  const allowMultiplePending = loanPolicies.allowMultiplePendingLoanApplications !== false;
  const maxPendingApplications = Number(loanPolicies.maxPendingApplicationsPerMember) || 1;
  const isBlockedByOutstanding = !allowWithOutstanding && outstandingLoanBalance > 0;
  const isBlockedByPending = !allowMultiplePending && pendingApplicationCount >= maxPendingApplications;
  const isBlocked = isBlockedByOutstanding || isBlockedByPending || hasActiveLoan;

  const eligibleSavings = deductOutstanding ? Math.max(0, teacherSavings - outstandingLoanBalance) : teacherSavings;
  const maxLoanFromEligible = eligibleSavings * maxLoanMultiplier;

  const defaultLoanType = useMemo(
    () => ({
      id: 0,
      name: "Monthly",
      displayName: "Standard Member Loan",
      description: "Standard loan for members with verified savings",
      interestRate: Number(groupSettings?.loanInterestRate || 12),
      defaultTermMonths: 12,
      maxTermMonths: 12,
      minAmount: 1000,
      maxAmount: 10000000,
      requiredInsurerCount: 0,
      isActive: true,
    }),
    [groupSettings]
  );

  const displayLoanTypes = loanTypes && loanTypes.length > 0 ? loanTypes : [defaultLoanType];

  const selectedLoanType = useMemo(
    () => displayLoanTypes.find((lt: any) => String(lt.id) === loanTypeId),
    [displayLoanTypes, loanTypeId]
  );

  // Classify all loan types into recommended / eligible / not_eligible
  const classifiedLoanTypes = useMemo((): ClassifiedLoanType[] => {
    if (!displayLoanTypes) return [];
    return displayLoanTypes.map((lt: any) => {
      const ltMin = Number(lt.minAmount) || 0;
      const ltMax = lt.maxAmount ? Number(lt.maxAmount) : Infinity;
      const ltInterest = Number(lt.interestRate) || 999;
      const memberMaxLoan = maxLoanFromEligible;
      const reasons: string[] = [];

      if (isBlocked) {
        if (isBlockedByOutstanding) reasons.push("Active loan balance must be cleared first");
        else if (isBlockedByPending) reasons.push("Pending application must be resolved first");
        else reasons.push("Active loan limit reached");
      }
      if (memberMaxLoan < ltMin) {
        reasons.push(`Savings too low — min ${formatCurrency(ltMin)}, your max: ${formatCurrency(memberMaxLoan)}`);
      }

      const isEligible = reasons.length === 0;
      let score = 0;
      if (isEligible) {
        score += Math.max(0, 100 - ltInterest * 4);
        score += Math.min(50, (ltMin / 500000) * 50);
        const dt = Number(lt.defaultTermMonths) || 12;
        score += Math.min(20, (dt / 24) * 20);
      }

      return {
        loanType: lt,
        category: isEligible ? "eligible" : "not_eligible",
        notEligibleReasons: reasons,
        score,
      };
    });
  }, [displayLoanTypes, isBlocked, isBlockedByOutstanding, isBlockedByPending, maxLoanFromEligible, formatCurrency]);

  // Find best eligible, then mark as "recommended"
  const recommendedClassification = useMemo(() => {
    const eligible = classifiedLoanTypes.filter((c) => c.category === "eligible");
    if (eligible.length === 0) return null;
    return eligible.reduce((best, curr) => {
      if (curr.score !== best.score) return curr.score > best.score ? curr : best;
      const aInt = Number(curr.loanType.interestRate) || 999;
      const bInt = Number(best.loanType.interestRate) || 999;
      if (aInt !== bInt) return aInt < bInt ? curr : best;
      const aTerm = Number(curr.loanType.defaultTermMonths) || 0;
      const bTerm = Number(best.loanType.defaultTermMonths) || 0;
      if (aTerm !== bTerm) return aTerm > bTerm ? curr : best;
      const aMax = Number(curr.loanType.maxAmount) || 0;
      const bMax = Number(best.loanType.maxAmount) || 0;
      return aMax > bMax ? curr : best;
    });
  }, [classifiedLoanTypes]);

  // Derive the final classified list with "recommended" set on the best one
  const finalClassifiedLoanTypes = useMemo(() => {
    if (!recommendedClassification) return classifiedLoanTypes;
    return classifiedLoanTypes.map((c) =>
      c.loanType.id === recommendedClassification.loanType.id
        ? { ...c, category: "recommended" as const }
        : c
    );
  }, [classifiedLoanTypes, recommendedClassification]);

  const eligibleLoanTypes = useMemo(
    () => finalClassifiedLoanTypes.filter((c) => c.category !== "not_eligible").map((c) => c.loanType),
    [finalClassifiedLoanTypes]
  );

  // Auto-select the recommended loan type when data loads
  useEffect(() => {
    if (loanTypeId !== "0") return;
    if (loanTypesLoading || contributionsLoading) return;
    if (isBlocked || !recommendedClassification) return;
    const best = recommendedClassification.loanType;
    setLoanTypeId(String(best.id));
    if (best.defaultTermMonths) setTermMonths(String(best.defaultTermMonths));
  }, [recommendedClassification, loanTypesLoading, contributionsLoading, isBlocked, loanTypeId]);

  // Auto-fill repayment term when loan type changes (if not already set by auto-select)
  useEffect(() => {
    if (loanTypeId !== "0" && selectedLoanType?.defaultTermMonths) {
      const currentTerm = parseInt(termMonths) || 0;
      if (currentTerm === 0 || currentTerm === 12) {
        setTermMonths(String(selectedLoanType.defaultTermMonths));
      }
    }
  }, [loanTypeId, selectedLoanType]);

  const interestRate = Number(selectedLoanType?.interestRate || groupSettings?.loanInterestRate || 12);
  const numericAmount = parseFloat(amount) || 0;
  const numericTerm = parseInt(termMonths) || 12;
  const totalRepayment = numericAmount * (1 + interestRate / 100);
  const monthlyInstallment = numericTerm > 0 ? totalRepayment / numericTerm : 0;

  const loanTypeMinAmount = Number(selectedLoanType?.minAmount) || 1000;
  const loanTypeMaxAmount = Number(selectedLoanType?.maxAmount) || 0;

  const effectiveMin = globalMinLoan > 0 ? Math.max(loanTypeMinAmount, globalMinLoan) : loanTypeMinAmount;
  const effectiveMax = (() => {
    const fromLoanType = loanTypeMaxAmount > 0 ? loanTypeMaxAmount : maxLoanFromEligible || 10000000;
    const fromPolicy = globalMaxLoan > 0 && globalMaxLoan < Infinity ? globalMaxLoan : Infinity;
    return Math.min(fromLoanType, fromPolicy);
  })();

  const amountValid = numericAmount === 0 || (numericAmount >= effectiveMin && numericAmount <= effectiveMax);
  const hasSavings = teacherSavings > 0;
  const loanTypeSelected = loanTypeId !== "0";

  const canNextStep0 = !!selectedMember && !isBlocked;
  const requiredGuarantorCount = selectedLoanType?.requiredInsurerCount || (selectedLoanType?.requiresInsurers ? 1 : 0);
  const guarantorsFilled = requiredGuarantorCount === 0 || (guarantors.length >= requiredGuarantorCount && guarantors.slice(0, requiredGuarantorCount).every((g) => g.name && g.memberCode));
  const canNextStep1 = amountValid && numericAmount > 0 && hasSavings && loanTypeSelected && !isBlocked && guarantorsFilled;
  const canNextStep2 = numericTerm >= 1 && numericTerm <= 60;

  const repaymentSchedule = useMemo(() => {
    if (numericAmount <= 0 || numericTerm <= 0) return [];
    const principalPortion = numericAmount / numericTerm;
    const totalInterest = totalRepayment - numericAmount;
    const interestPortion = numericTerm > 0 ? totalInterest / numericTerm : 0;
    const schedule = [];
    let balance = numericAmount;
    const startDate = new Date();
    for (let i = 1; i <= numericTerm; i++) {
      const dueDate = new Date(startDate);
      dueDate.setMonth(dueDate.getMonth() + i);
      const amountDue = principalPortion + interestPortion;
      balance -= principalPortion;
      schedule.push({
        installment: i,
        dueDate: format(dueDate, "MMM dd, yyyy"),
        principal: Math.round(principalPortion),
        interest: Math.round(interestPortion),
        balance: Math.round(Math.max(balance, 0)),
        amountDue: Math.round(amountDue),
      });
    }
    return schedule;
  }, [numericAmount, numericTerm, totalRepayment]);

  const orgInfo = useMemo(() => {
    const org = orgSettings?.organization || {};
    return {
      name: org.cooperativeName || groupSettings?.cooperativeName || "CAISSE D'ENTRAIDE IPRC KARONGI",
      registrationNumber: org.registrationNumber || "N/A",
      address: org.physicalAddress || "Karongi, Rwanda",
      phone: org.contactPhone || "+250 793 502 827",
      email: org.contactEmail || "caisse.iprcwest@gmail.com",
    };
  }, [orgSettings, groupSettings]);

  const processingFee = Math.round(numericAmount * 0.01);
  const insuranceFee = Math.round(numericAmount * 0.005);

  // ─── Print Styles ───
  useEffect(() => {
    const style = document.createElement("style");
    style.id = "admin-loan-print-styles";
    style.textContent = `
      @media print {
        @page { size: A4 portrait; margin: 12mm; }
        body * { visibility: hidden !important; }
        .loan-review-print-area, .loan-review-print-area * { visibility: visible !important; }
        .loan-review-print-area { position: absolute; left: 0; top: 0; width: 100%; background: white !important; }
        .loan-review-print-area .print\\:hidden { display: none !important; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById("admin-loan-print-styles");
      if (el) el.remove();
    };
  }, []);

  // ─── Handlers ───

  const handleMemberSelect = (teacher: any) => {
    setSelectedMember(teacher);
    setMemberPopoverOpen(false);
    setStep(1);
    setLoanTypeId("0");
    setAmount("");
    setPurpose("");
    setTermMonths("12");
    setDeclarationChecked(false);
  };

  const handleSubmit = async (force = false) => {
    if (!force && isBlocked) {
      if (isBlockedByOutstanding) {
        toast.error("Member has an active loan balance. Cannot apply for a new loan until cleared.");
      } else if (isBlockedByPending) {
        toast.error("Member already has a pending loan application. Please wait for approval or rejection.");
      } else {
        toast.error("Active loan limit reached. Cannot apply for a new loan.");
      }
      return;
    }
    setSubmitting(true);
    try {
      const selectedType = displayLoanTypes.find((lt: any) => String(lt.id) === loanTypeId);
      const loanTypeValue = selectedType?.name || "Monthly";

      const cleanPayload: Record<string, any> = {
        amount: parseInt(String(Math.round(Number(numericAmount))), 10),
        termMonths: parseInt(String(Number(numericTerm)), 10),
        purpose: purpose || "General",
        loanType: loanTypeValue,
        teacherId: selectedMemberId,
        totalSavings: parseInt(String(Math.round(Number(teacherSavings))), 10),
        eligibleSavings: parseInt(String(Math.round(Number(eligibleSavings))), 10),
        maxLoanAmount: parseInt(String(Math.round(Number(maxLoanFromEligible))), 10),
        force,
      };
      if (loanTypeId && parseInt(loanTypeId, 10) > 0) {
        cleanPayload.loanTypeId = parseInt(loanTypeId, 10);
      }

      if (guarantors.length > 0) {
        cleanPayload.guarantors = guarantors.map((g) => ({
          name: g.name,
          memberId: g.memberId || undefined,
          memberCode: g.memberCode || undefined,
          phone: g.phone || undefined,
          email: g.email || undefined,
        }));
      }

      await api.post("/loans/apply", cleanPayload);
      toast.success("Loan application submitted successfully for " + selectedMember?.label);
      setStep(4);
      queryClient.invalidateQueries({ queryKey: ["loans"], exact: false });
    } catch (err: any) {
      const response = err?.response?.data;
      const code = response?.code;

      if (code === "ACTIVE_LOAN_EXISTS" || code === "DUPLICATE_APPLICATION") {
        setConflictCode(code);
        setConflictData(response.data);
        setConflictOpen(true);
      } else {
        const msg = response?.message || response?.error || "Failed to submit application. Please try again.";
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAnyway = () => {
    setConflictOpen(false);
    handleSubmit(true);
  };

  const userRole = user?.leadershipRole || user?.role?.name || user?.role;
  const isAdminOrPresident = userRole === "President" || userRole === "Admin" || userRole === "administrator";

  return (
    <div className="sm:px-4 px-3 pt-3">
      {/* Conflict Modal */}
      {conflictData && (
        <LoanConflictModal
          isOpen={conflictOpen}
          onClose={() => setConflictOpen(false)}
          conflictCode={conflictCode}
          conflictData={conflictData}
          onSubmitAnyway={handleSubmitAnyway}
          isAdmin={isAdminOrPresident}
          isPresident={userRole === "President"}
        />
      )}

      {/* ─── Header ─── */}
      <div className="mb-6">
        <button
          onClick={() => {
            if (step > 0 && step < 4) setStep((step - 1) as Step);
            else navigate("/loans");
          }}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {step === 0 ? "Back to Loans" : "Previous step"}
        </button>
        <h1 className="text-xl font-bold text-slate-900">Apply for Loan</h1>
        <p className="text-sm text-slate-500">Create a loan request for a cooperative member</p>
      </div>

      {/* ─── Step Indicator ─── */}
      {step > 0 && step < 4 && (
        <div className="flex items-center gap-2 mb-6">
          {stepLabels.map((label, i) => (
            <div key={label} className="flex items-center gap-2 flex-1">
              <div
                className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium shrink-0 ${
                  step === i
                    ? "bg-[#009A44] text-white"
                    : step > i
                    ? "bg-green-100 text-[#009A44]"
                    : "bg-slate-100 text-slate-400"
                }`}
              >
                {step > i ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </div>
              <div className={`h-0.5 flex-1 ${step > i ? "bg-[#009A44]" : "bg-slate-200"}`} />
            </div>
          ))}
        </div>
      )}

      {/* ═══════════ Step 0: Select Member ═══════════ */}
      {step === 0 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-5">
            <div>
              <h2 className="text-base font-semibold text-slate-900 mb-1">Select Member</h2>
              <p className="text-xs text-slate-500">Choose the member to apply for a loan on behalf of</p>
            </div>

            <div className="max-w-lg">
              <label className="text-sm font-medium mb-1.5 block">Member</label>
              <Popover open={memberPopoverOpen} onOpenChange={setMemberPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={memberPopoverOpen}
                    className="w-full justify-between font-normal h-11"
                  >
                    {selectedMember ? (
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarFallback className="text-xs">
                            {selectedMember.label?.charAt(0) || "?"}
                          </AvatarFallback>
                        </Avatar>
                        <span>{selectedMember.label}</span>
                      </div>
                    ) : (
                      "Search member by name or ID..."
                    )}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search by name or Member ID..." />
                    <CommandList>
                      {isTeachersLoading ? (
                        <div className="py-6 text-center text-sm text-muted-foreground">Loading...</div>
                      ) : teachers.length === 0 ? (
                        <CommandEmpty>No members found</CommandEmpty>
                      ) : (
                        <CommandGroup>
                          {teachers.map((teacher: any) => (
                            <CommandItem
                              key={teacher.id}
                              value={`${teacher.label} ${teacher.memberCode || ""}`}
                              onSelect={() => handleMemberSelect(teacher)}
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <Avatar className="h-7 w-7">
                                  <AvatarFallback className="text-xs">
                                    {teacher.label?.charAt(0) || "?"}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <div className="text-sm font-medium">{teacher.label}</div>
                                  {teacher.memberCode && !String(teacher.memberCode).startsWith("TMP-") && (
                                    <div className="text-xs text-gray-500">Member ID: {teacher.memberCode}</div>
                                  )}
                                </div>
                              </div>
                              {teacher.hasActiveLoan && (
                                <Badge variant="secondary" className="text-[10px] bg-amber-100 text-amber-700 ml-1">
                                  Active Loan
                                </Badge>
                              )}
                              {selectedMember?.id === teacher.id && (
                                <Check className="ml-auto h-4 w-4" />
                              )}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Selected Member Info */}
            {selectedMember && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-lg"
              >
                <Card className="border-green-200 bg-green-50/40">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback>{selectedMember.label?.charAt(0) || "?"}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-sm">{selectedMember.label}</p>
                        {memberInfo.memberNumber !== "N/A" && (
                          <p className="text-xs text-gray-500">Member ID: {memberInfo.memberNumber}</p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {memberInfo.phone && memberInfo.phone !== "Not provided" && (
                        <div className="text-gray-600 flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          <span>{memberInfo.phone}</span>
                        </div>
                      )}
                      {memberInfo.email && memberInfo.email !== "Not provided" && (
                        <div className="text-gray-600 flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          <span>{memberInfo.email}</span>
                        </div>
                      )}
                      <div className="col-span-2">
                        {selectedMember.hasActiveLoan ? (
                          <div className="flex items-center gap-1.5 text-amber-600">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            <span className="font-medium">Has active loan - cannot apply for new loan</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 text-green-600">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            <span className="font-medium">Eligible for loan application</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            <div className="flex justify-end pt-2">
              <Button
                disabled={!canNextStep0}
                onClick={() => setStep(1)}
                className="bg-[#009A44] hover:bg-[#008238]"
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ═══════════ Step 1: Loan Details ═══════════ */}
      {step === 1 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-5">
            <div>
              <h2 className="text-base font-semibold text-slate-900 mb-1">Loan Details</h2>
              <p className="text-xs text-slate-500">Configure loan type and amount for {selectedMember?.label}</p>
            </div>

            {/* Savings & Loan Eligibility */}
            {contributionsLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 p-3 rounded-lg">
                <Loader className="h-4 w-4 animate-spin" />
                Loading member savings...
              </div>
            ) : (
              <div className={`border rounded-lg p-3 space-y-1.5 ${isBlocked ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}>
                <div className="flex justify-between text-sm">
                  <span className={isBlocked ? "text-red-800" : "text-green-800"}>Member Savings:</span>
                  <span className={`font-semibold ${isBlocked ? "text-red-900" : "text-green-900"}`}>{formatCurrency(teacherSavings)}</span>
                </div>
                {pendingApplicationCount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-amber-800 font-medium">Pending Applications:</span>
                    <span className="font-semibold text-amber-600">{pendingApplicationCount}</span>
                  </div>
                )}
                {outstandingLoanBalance > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-red-800 font-medium">Active Loan Balance:</span>
                    <span className="font-semibold text-red-600">{formatCurrency(outstandingLoanBalance)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className={isBlocked ? "text-red-800" : "text-green-800"}>Eligible Savings:</span>
                  <span className={`font-semibold ${isBlocked ? "text-red-900" : "text-green-900"}`}>{formatCurrency(eligibleSavings)}</span>
                </div>
                <Separator className={`my-1.5 ${isBlocked ? "bg-red-200" : ""}`} />
                <div className="flex justify-between text-sm">
                  <span className={isBlocked ? "text-red-800" : "text-green-800"}>Maximum Loan ({maxLoanMultiplier}x):</span>
                  <span className={`font-semibold ${isBlocked ? "text-red-900" : "text-green-900"}`}>{formatCurrency(maxLoanFromEligible)}</span>
                </div>
                {isBlockedByPending && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-100 p-2 rounded mt-1">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    <span className="font-medium">This member already has a pending loan application awaiting approval.</span>
                  </div>
                )}
                {outstandingLoanBalance > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-red-700 bg-red-100 p-2 rounded mt-1">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    <span className="font-medium">Member has an active loan balance of {formatCurrency(outstandingLoanBalance)}. Cannot apply for a new loan until cleared.</span>
                  </div>
                )}
                {teacherSavings === 0 && outstandingLoanBalance === 0 && pendingApplicationCount === 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 p-2 rounded mt-1">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    <span>This member has no eligible savings for a loan.</span>
                  </div>
                )}
                {hasActiveLoan && !outstandingLoanBalance && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 p-2 rounded mt-1">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    <span>This member already has an active loan. Only one active loan allowed.</span>
                  </div>
                )}
              </div>
            )}

            {/* Loan Type Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Loan Type</label>
              {loanTypesLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader className="h-4 w-4 animate-spin" />
                  Loading loan types...
                </div>
              ) : displayLoanTypes.length === 0 ? (
                <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 p-2 rounded">
                  <Info className="h-3.5 w-3.5 shrink-0" />
                  <span>No active loan types found. Please configure a loan type in settings.</span>
                </div>
              ) : (
                <>
                  <Select value={loanTypeId} onValueChange={(val) => setLoanTypeId(val)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select loan type" />
                    </SelectTrigger>
                    <SelectContent>
                      {displayLoanTypes.map((lt: any) => {
                        const ltMax = lt.maxAmount ? Number(lt.maxAmount) : null;
                        const cls = finalClassifiedLoanTypes.find((c) => String(c.loanType.id) === String(lt.id));
                        const isRecommended = cls?.category === "recommended";
                        const isNotEligible = cls?.category === "not_eligible";
                        return (
                          <SelectItem key={lt.id} value={String(lt.id)} disabled={isNotEligible}>
                            <div className="flex flex-col py-0.5">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{lt.displayName || lt.name}</span>
                                {isRecommended && (
                                  <span className="inline-flex items-center rounded-full bg-green-100 text-green-700 px-1.5 py-0.5 text-[9px] font-semibold leading-none">
                                    Recommended
                                  </span>
                                )}
                                {isNotEligible && (
                                  <span className="inline-flex items-center rounded-full bg-red-100 text-red-600 px-1.5 py-0.5 text-[9px] font-semibold leading-none">
                                    Not eligible
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-[10px] text-slate-500 mt-0.5">
                                <span>{Number(lt.interestRate || 0)}% interest</span>
                                {ltMax && <span>• Up to {formatCurrency(ltMax)}</span>}
                                {lt.maxTermMonths && <span>• {lt.maxTermMonths} mo</span>}
                              </div>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {!isBlocked && eligibleLoanTypes.length === 0 && loanTypeId === "0" && (
                    <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 p-2 rounded mt-1.5">
                      <Info className="h-3.5 w-3.5 shrink-0" />
                      <span>This member is not eligible for any loan type based on current savings ({formatCurrency(teacherSavings)}). Maximum eligible loan: {formatCurrency(maxLoanFromEligible)}.</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Available Loan Options — Card Comparison */}
            {!loanTypesLoading && finalClassifiedLoanTypes.length > 0 && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-slate-700">Available Loan Options</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {finalClassifiedLoanTypes.map((classified) => {
                    const lt = classified.loanType;
                    const isSelected = String(lt.id) === loanTypeId;
                    const isRecommended = classified.category === "recommended";
                    const isDisabled = classified.category === "not_eligible";
                    const ltMin = Number(lt.minAmount) || 0;
                    const ltMax = lt.maxAmount ? Number(lt.maxAmount) : null;
                    const ltInterest = Number(lt.interestRate) || 0;

                    return (
                      <button
                        key={lt.id}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => {
                          if (!isDisabled) {
                            setLoanTypeId(String(lt.id));
                            if (lt.defaultTermMonths) setTermMonths(String(lt.defaultTermMonths));
                          }
                        }}
                        className={cn(
                          "relative text-left rounded-lg border p-4 transition-all",
                          isSelected && !isDisabled
                            ? "border-[#009A44] bg-green-50 ring-1 ring-[#009A44]"
                            : isDisabled
                            ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
                            : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 cursor-pointer"
                        )}
                      >
                        {isRecommended && (
                          <span className="absolute -top-2.5 left-3 inline-flex items-center rounded-full bg-[#009A44] text-white px-2 py-0.5 text-[10px] font-semibold leading-none shadow-sm">
                            ★ Recommended
                          </span>
                        )}
                        {isSelected && !isDisabled && (
                          <div className="absolute top-3 right-3">
                            <CheckCircle2 className="h-5 w-5 text-[#009A44]" />
                          </div>
                        )}
                        <p className={cn("text-sm font-semibold", isDisabled ? "text-slate-500" : "text-slate-900")}>
                          {lt.displayName || lt.name}
                        </p>
                        <p className={cn("text-lg font-bold mt-1", isDisabled ? "text-slate-400" : "text-[#009A44]")}>
                          {ltInterest}% <span className="text-xs font-normal text-slate-500">interest</span>
                        </p>
                        <div className="mt-2 text-xs text-slate-500 space-y-0.5">
                          <p>
                            Min: {formatCurrency(ltMin)}
                            {ltMax && ` · Max: ${formatCurrency(ltMax)}`}
                          </p>
                          {lt.maxTermMonths && <p>Up to {formatTerm(lt.maxTermMonths)}</p>}
                          {lt.defaultTermMonths && <p>Default: {formatTerm(lt.defaultTermMonths)}</p>}
                        </div>
                        {isDisabled && classified.notEligibleReasons.length > 0 && (
                          <div className="mt-2 text-[11px] text-red-500 space-y-0.5">
                            {classified.notEligibleReasons.map((reason, i) => (
                              <p key={i} className="flex items-start gap-1">
                                <XCircle className="h-3 w-3 mt-0.5 shrink-0" />
                                {reason}
                              </p>
                            ))}
                          </div>
                        )}
                        {!isDisabled && (lt.requiredInsurerCount || lt.requiresInsurers) && (
                          <p className="mt-2 text-[11px] text-slate-400">
                            Requires {lt.requiredInsurerCount || 1} guarantor{lt.requiredInsurerCount !== 1 ? "s" : ""}
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Selected Loan Type Details Card */}
            {selectedLoanType && (
              <div className="border border-green-200 rounded-lg overflow-hidden">
                <div className="bg-green-50 px-4 py-3 border-b border-green-100 flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-[#009A44]" />
                  <span className="font-semibold text-slate-900">{selectedLoanType.name}</span>
                  {recommendedClassification?.loanType.id === selectedLoanType.id && (
                    <span className="inline-flex items-center rounded-full bg-green-100 text-green-700 px-2 py-0.5 text-[10px] font-semibold">
                      Recommended
                    </span>
                  )}
                  {selectedLoanType.description && (
                    <span className="text-xs text-slate-500 ml-1">- {selectedLoanType.description}</span>
                  )}
                </div>
                <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Interest Rate</p>
                    <p className="text-sm font-semibold text-slate-900">{interestRate}%</p>
                  </div>
                  {selectedLoanType.minAmount > 0 && (
                    <div>
                      <p className="text-xs text-slate-500">Minimum Amount</p>
                      <p className="text-sm font-semibold text-slate-900">{formatCurrency(selectedLoanType.minAmount)}</p>
                    </div>
                  )}
                  {selectedLoanType.maxAmount && (
                    <div>
                      <p className="text-xs text-slate-500">Maximum Amount</p>
                      <p className="text-sm font-semibold text-slate-900">{formatCurrency(effectiveMax)}</p>
                    </div>
                  )}
                  {selectedLoanType.maxTermMonths && (
                    <div>
                      <p className="text-xs text-slate-500">Maximum Term</p>
                      <p className="text-sm font-semibold text-slate-900">{formatTerm(selectedLoanType.maxTermMonths)}</p>
                    </div>
                  )}
                  {(selectedLoanType.requiredInsurerCount || selectedLoanType.requiresInsurers) && (
                    <div>
                      <p className="text-xs text-slate-500">Required Guarantors</p>
                      <p className="text-sm font-semibold text-slate-900">
                        {selectedLoanType.requiredInsurerCount || 1}{" "}
                        {selectedLoanType.requiredInsurerCount === 1 ? "person" : "people"}
                      </p>
                    </div>
                  )}
                  {selectedLoanType.defaultTermMonths && (
                    <div>
                      <p className="text-xs text-slate-500">Suggested Term</p>
                      <p className="text-sm font-semibold text-slate-900">{formatTerm(selectedLoanType.defaultTermMonths)}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* How it works */}
            {selectedLoanType && (
              <div className={`border rounded-lg p-3 ${isBlocked ? "bg-red-50 border-red-200" : "bg-blue-50 border-blue-200"}`}>
                <div className="flex items-start gap-2">
                  {isBlocked ? (
                    <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                  ) : (
                    <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                  )}
                  <div>
                    <p className={`text-sm font-medium ${isBlocked ? "text-red-800" : "text-blue-800"}`}>
                      {isBlocked ? "Loan not available" : "How it works"}
                    </p>
                    <p className={`text-xs mt-1 ${isBlocked ? "text-red-700" : "text-blue-700"}`}>
                      {isBlockedByOutstanding
                        ? `This member has an active loan balance of ${formatCurrency(outstandingLoanBalance)}. They are not eligible for a new loan until the balance is cleared.`
                        : isBlockedByPending
                        ? `This member already has a pending loan application awaiting approval. Please wait for approval or rejection before applying again.`
                        : `Based on the member's current savings of ${formatCurrency(teacherSavings)}, they can borrow up to ${formatCurrency(maxLoanFromEligible)} (${maxLoanMultiplier}x savings).${selectedLoanType.description ? ` ${selectedLoanType.description}` : ""} Interest is charged at ${interestRate}% and repayment is made in monthly installments.`
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Eligibility Checks */}
            {loanTypeSelected && (
              <div className="border border-slate-200 rounded-lg p-4 space-y-2">
                <p className="text-sm font-semibold text-slate-700 mb-2">Eligibility Checks</p>
                <div className="flex items-center gap-2">
                  {hasSavings ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500 shrink-0" />
                  )}
                  <span className={`text-xs ${hasSavings ? "text-green-700" : "text-red-700"}`}>
                    Active savings balance ({formatCurrency(teacherSavings)})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {!isBlocked ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500 shrink-0" />
                  )}
                  <span className={`text-xs ${!isBlocked ? "text-green-700" : "text-red-700"}`}>
                    {isBlockedByOutstanding
                      ? `Active loan balance: ${formatCurrency(outstandingLoanBalance)}. Cannot apply until cleared.`
                      : isBlockedByPending
                      ? "Pending loan application exists. Awaiting approval."
                      : isBlocked
                      ? "Active loan limit reached. Only one active loan allowed."
                      : "No active loan restrictions"}
                  </span>
                </div>
                {selectedLoanType && (
                  <div className="flex items-center gap-2">
                    {numericAmount > 0 && amountValid ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                    ) : (
                      <XCircle className="h-4 w-4 text-slate-400 shrink-0" />
                    )}
                    <span
                      className={`text-xs ${numericAmount > 0 && amountValid ? "text-green-700" : "text-slate-500"}`}
                    >
                      {numericAmount > 0
                        ? `Amount between ${formatCurrency(effectiveMin)} and ${formatCurrency(effectiveMax)}`
                        : `Allowed range: ${formatCurrency(effectiveMin)} - ${formatCurrency(effectiveMax)}`}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Loan Amount */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Loan Amount (FRW)</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="number"
                  placeholder="e.g. 500000"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pl-9"
                  min="1000"
                  disabled={isBlocked}
                />
              </div>
              {isBlocked ? (
                <p className="text-xs text-red-500 mt-1 font-medium">
                  You cannot apply for a new loan while you have an outstanding loan balance.
                </p>
              ) : selectedLoanType ? (
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-slate-500">Min: {formatCurrency(effectiveMin)}</p>
                  <p className="text-xs text-slate-500">Max: {formatCurrency(effectiveMax)}</p>
                </div>
              ) : null}
              {numericAmount > 0 && (
                <>
                  {numericAmount < effectiveMin && (
                    <p className="text-xs text-red-500 mt-1">
                      Minimum loan amount is {formatCurrency(effectiveMin)}
                    </p>
                  )}
                  {numericAmount > maxLoanFromEligible && (
                    <p className="text-xs text-red-500 mt-1">
                      Amount exceeds maximum eligible loan of {formatCurrency(maxLoanFromEligible)}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Estimated Repayment Preview */}
            {numericAmount > 0 && selectedLoanType && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                  <ShieldCheck className="h-4 w-4 text-[#009A44]" />
                  Estimated Repayment
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-500">Interest Rate</p>
                    <p className="text-sm font-semibold text-slate-900">{interestRate}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Est. Monthly Payment</p>
                    <p className="text-sm font-semibold text-[#009A44]">{formatCurrency(Math.round(monthlyInstallment))}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Total Repayment</p>
                    <p className="text-sm font-semibold text-slate-900">{formatCurrency(Math.round(totalRepayment))}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Interest Cost</p>
                    <p className="text-sm font-semibold text-slate-900">
                      {formatCurrency(Math.round(totalRepayment - numericAmount))}
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  * Based on {numericTerm}-month term. You can adjust the repayment period in the next step.
                </p>
              </div>
            )}

            {/* Purpose */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Purpose of Loan</label>
              <Textarea
                placeholder="e.g. School fees, business investment, home improvement..."
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                rows={3}
              />
            </div>

            {/* Guarantors */}
            {selectedLoanType && (
              <GuarantorFields
                guarantors={guarantors}
                setGuarantors={setGuarantors}
                requiredCount={selectedLoanType.requiredInsurerCount || 0}
                maxCount={Math.max(selectedLoanType.requiredInsurerCount || 3, 3)}
                isAdmin={true}
              />
            )}

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(0)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button
                disabled={!canNextStep1}
                onClick={() => setStep(2)}
                className="bg-[#009A44] hover:bg-[#008238]"
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ═══════════ Step 2: Repayment Plan ═══════════ */}
      {step === 2 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-5">
            <div>
              <h2 className="text-base font-semibold text-slate-900 mb-1">Repayment Plan</h2>
              <p className="text-xs text-slate-500">Choose the repayment duration</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Duration (months)</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="number"
                  placeholder="e.g. 12"
                  value={termMonths}
                  onChange={(e) => setTermMonths(e.target.value)}
                  className="pl-9"
                  min="1"
                  max="60"
                />
              </div>
              {selectedLoanType?.maxTermMonths && (
                <p className="text-xs text-slate-500 mt-1">
                  Maximum term for this loan type: {formatTerm(selectedLoanType.maxTermMonths)}
                </p>
              )}
              <p className="text-xs text-slate-500 mt-1">Choose between 1 and 60 months</p>
            </div>

            {/* Installment Preview */}
            {numericAmount > 0 && numericTerm > 0 && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <p className="text-sm font-semibold text-slate-700">Installment Preview</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-500">Loan Amount</p>
                    <p className="text-sm font-semibold">{formatCurrency(numericAmount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Interest Rate</p>
                    <p className="text-sm font-semibold">{interestRate}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Duration</p>
                    <p className="text-sm font-semibold">{numericTerm} months</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Monthly Payment</p>
                    <p className="text-sm font-semibold text-[#009A44]">{formatCurrency(Math.round(monthlyInstallment))}</p>
                  </div>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between">
                    <span className="text-xs text-slate-500">Total Repayment</span>
                    <span className="text-sm font-bold">{formatCurrency(Math.round(totalRepayment))}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button
                disabled={!canNextStep2}
                onClick={() => setStep(3)}
                className="bg-[#009A44] hover:bg-[#008238]"
              >
                Review Application <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ═══════════ Step 3: Review & Confirm ═══════════ */}
      {step === 3 && (
        <div ref={printRef} className="loan-review-print-area">
          {/* ─── Screen Header ─── */}
          <div className="print:hidden mb-4">
            <button
              onClick={() => setStep(2)}
              className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Previous step
            </button>
          </div>

          <div className="print:hidden flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h1 className="text-xl font-bold text-slate-900">Review Loan Application</h1>
              <p className="text-sm text-slate-500">
                Please review all details carefully before submitting it for approval.
              </p>
            </div>
            <Badge variant="secondary" className="text-sm px-3 py-1 w-fit">
              <Clock className="h-3.5 w-3.5 mr-1 inline" />
              Draft
            </Badge>
          </div>

          {/* ─── Top Action Buttons ─── */}
          <div className="print:hidden flex flex-wrap gap-2 mb-6">
            <Button
              size="sm"
              className="bg-[#009A44] hover:bg-[#008238] text-white ml-auto gap-1.5"
              disabled={submitting || !declarationChecked}
              onClick={() => handleSubmit()}
            >
              {submitting ? (
                <>
                  <Loader className="animate-spin h-4 w-4" /> Submitting...
                </>
              ) : (
                <>
                  <ShieldCheck className="h-4 w-4" /> Submit Application
                </>
              )}
            </Button>
          </div>

          {/* ─── Main Application Document ─── */}
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            {/* ── Cooperative Header ── */}
            <div className="flex items-center gap-4 p-5 sm:p-6 border-b border-slate-200">
              <div className="w-14 h-14 sm:w-16 sm:h-16 flex shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-50 border">
                <span className="text-xs font-bold text-green-700">CEIK</span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-bold text-slate-900">{orgInfo.name}</h3>
                <p className="text-xs text-slate-500">Reg: {orgInfo.registrationNumber}</p>
                <p className="text-xs text-slate-500">
                  {orgInfo.address} | {orgInfo.phone} | {orgInfo.email}
                </p>
              </div>
            </div>

            {/* ── Title ── */}
            <div className="text-center py-3 border-b border-slate-200 bg-slate-50/50">
              <h2 className="text-lg font-bold text-[#009A44]">Loan Application Summary</h2>
            </div>

            {/* ── Applicant Information ── */}
            <div className="print-break-inside">
              <div className="border-b border-slate-200 px-5 sm:px-6 py-3 bg-green-50/30">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <User className="h-4 w-4 text-[#009A44]" />
                  Applicant Information
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 px-5 sm:px-6 py-4 text-sm">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
                    <User className="h-4 w-4 text-[#009A44]" />
                  </div>
                  <div className="min-w-0">
                    <span className="text-slate-500">Full Name:</span>{" "}
                    <span className="font-semibold text-slate-800 ml-1">{memberInfo.fullName}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                    <BadgeCheck className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <span className="text-slate-500">Member Number:</span>{" "}
                    <span className="font-semibold text-slate-800 ml-1">{memberInfo.memberNumber}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
                    <Phone className="h-4 w-4 text-[#009A44]" />
                  </div>
                  <div className="min-w-0">
                    <span className="text-slate-500">Phone Number:</span>{" "}
                    <span className="font-semibold text-slate-800 ml-1">{memberInfo.phone}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                    <Mail className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <span className="text-slate-500">Email:</span>{" "}
                    <span className="font-semibold text-slate-800 ml-1">{memberInfo.email}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
                    <Wallet className="h-4 w-4 text-[#009A44]" />
                  </div>
                  <div className="min-w-0">
                    <span className="text-slate-500">Savings Balance:</span>{" "}
                    <span className="font-semibold text-[#009A44] ml-1">{formatCurrency(teacherSavings)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                    <TrendingUp className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <span className="text-slate-500">Maximum Eligible Loan:</span>{" "}
                    <span className="font-semibold text-slate-800 ml-1">
                      {formatCurrency(maxLoanFromEligible)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* ── Loan Details ── */}
            <div className="print-break-inside">
              <div className="border-b border-slate-200 px-5 sm:px-6 py-3 bg-green-50/30">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-[#009A44]" />
                  Loan Details
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 px-5 sm:px-6 py-4 text-sm">
                <div>
                  <span className="text-slate-500">Loan Type:</span>{" "}
                  <span className="font-semibold text-slate-800 ml-1">
                    {selectedLoanType?.displayName || selectedLoanType?.name || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Requested Amount:</span>{" "}
                  <span className="font-semibold text-slate-800 ml-1">{formatCurrency(numericAmount)}</span>
                </div>
                <div>
                  <span className="text-slate-500">Interest Rate:</span>{" "}
                  <span className="font-semibold text-slate-800 ml-1">{interestRate}%</span>
                </div>
                <div>
                  <span className="text-slate-500">Loan Duration:</span>{" "}
                  <span className="font-semibold text-slate-800 ml-1">{formatTerm(numericTerm)}</span>
                </div>
                <div>
                  <span className="text-slate-500">Purpose:</span>{" "}
                  <span className="font-semibold text-slate-800 ml-1">{purpose || "General"}</span>
                </div>
                <div>
                  <span className="text-slate-500">Processing Fee:</span>{" "}
                  <span className="font-semibold text-slate-800 ml-1">{formatCurrency(processingFee)}</span>
                </div>
              </div>
            </div>

            <Separator />

            {/* ── Financial Summary ── */}
            <div className="print-break-inside">
              <div className="border-b border-slate-200 px-5 sm:px-6 py-3 bg-green-50/30">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-[#009A44]" />
                  Financial Summary
                </h3>
              </div>
              <div className="px-5 sm:px-6 py-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="bg-green-50 rounded-lg border border-green-200 p-3 text-center">
                    <p className="text-xs text-slate-500 mb-0.5">Requested Amount</p>
                    <p className="text-lg font-bold text-[#009A44]">{formatCurrency(numericAmount)}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg border border-green-200 p-3 text-center">
                    <p className="text-xs text-slate-500 mb-0.5">Monthly Installment</p>
                    <p className="text-lg font-bold text-[#009A44]">
                      {formatCurrency(Math.round(monthlyInstallment))}
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg border border-green-200 p-3 text-center">
                    <p className="text-xs text-slate-500 mb-0.5">Total Repayment</p>
                    <p className="text-lg font-bold text-[#009A44]">
                      {formatCurrency(Math.round(totalRepayment))}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* ── Repayment Schedule Preview ── */}
            {repaymentSchedule.length > 0 && (
              <div className="print-break-inside">
                <div className="border-b border-slate-200 px-5 sm:px-6 py-3 bg-green-50/30">
                  <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-[#009A44]" />
                    Repayment Schedule Preview
                  </h3>
                </div>
                <div className="px-5 sm:px-6 py-4 overflow-x-auto">
                  <table className="w-full text-xs sm:text-sm border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left py-2 px-2 font-semibold text-slate-600">#</th>
                        <th className="text-left py-2 px-2 font-semibold text-slate-600">Due Date</th>
                        <th className="text-right py-2 px-2 font-semibold text-slate-600">Principal</th>
                        <th className="text-right py-2 px-2 font-semibold text-slate-600">Interest</th>
                        <th className="text-right py-2 px-2 font-semibold text-slate-600">Balance</th>
                        <th className="text-right py-2 px-2 font-semibold text-slate-600">Amount Due</th>
                      </tr>
                    </thead>
                    <tbody>
                      {repaymentSchedule.slice(0, Math.min(6, repaymentSchedule.length)).map((row) => (
                        <tr
                          key={row.installment}
                          className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                        >
                          <td className="py-2 px-2 text-slate-600">{row.installment}</td>
                          <td className="py-2 px-2 text-slate-700 font-medium">{row.dueDate}</td>
                          <td className="py-2 px-2 text-right text-slate-700">
                            {Math.round(row.principal).toLocaleString()}
                          </td>
                          <td className="py-2 px-2 text-right text-slate-700">
                            {Math.round(row.interest).toLocaleString()}
                          </td>
                          <td className="py-2 px-2 text-right font-medium text-slate-700">
                            {Math.round(row.balance).toLocaleString()}
                          </td>
                          <td className="py-2 px-2 text-right font-semibold text-[#009A44]">
                            {Math.round(row.amountDue).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {repaymentSchedule.length > 6 && (
                    <p className="text-xs text-slate-400 text-center mt-3 italic">
                      ...remaining {repaymentSchedule.length - 6} repayments continue until loan maturity.
                    </p>
                  )}
                </div>
              </div>
            )}

            <Separator />

            {/* ── Eligibility Verification ── */}
            <div className="print-break-inside">
              <div className="border-b border-slate-200 px-5 sm:px-6 py-3 bg-green-50/30">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-[#009A44]" />
                  Eligibility Verification
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 px-5 sm:px-6 py-4 text-sm">
                {[
                  { label: "Loan amount within limit", ok: numericAmount > 0 && amountValid && numericAmount <= maxLoanFromEligible },
                  { label: "Savings requirement satisfied", ok: teacherSavings > 0 },
                  { label: "No active loan outstanding", ok: !isBlocked },
                  { label: "Loan type selected", ok: loanTypeSelected },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    {item.ok ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-orange-500 shrink-0" />
                    )}
                    <span className={`text-sm ${item.ok ? "text-green-700" : "text-orange-700"}`}>
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* ── Important Loan Terms ── */}
            <div className="print-break-inside">
              <div className="px-5 sm:px-6 py-4 bg-amber-50/40 border-b border-amber-100">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-3">
                  <Info className="h-4 w-4 text-amber-600" />
                  Important Loan Terms
                </h3>
                <ul className="space-y-1.5">
                  {[
                    "Late repayment penalties apply as per cooperative policy.",
                    "Processing fee is non-refundable once the application is processed.",
                    "Loan approval depends on committee review and credit assessment.",
                    "Loan disbursement is subject to verification of provided information.",
                    "Providing false information may lead to rejection and disciplinary action.",
                  ].map((term, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs sm:text-sm text-slate-700">
                      <span className="text-amber-600 mt-0.5 shrink-0">•</span>
                      {term}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <Separator />

            {/* ── Member Declaration ── */}
            <div className="print-break-inside">
              <div className="mx-5 sm:mx-6 my-4 border-2 border-green-200 rounded-lg bg-green-50/50 p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="declaration"
                    checked={declarationChecked}
                    onCheckedChange={(val) => setDeclarationChecked(val === true)}
                    className="mt-0.5"
                  />
                  <label
                    htmlFor="declaration"
                    className="text-sm text-slate-700 leading-relaxed cursor-pointer select-none"
                  >
                    <span className="font-semibold text-slate-800">I certify</span> that the information provided in
                    this application is accurate and complete. I understand that providing false information may result
                    in rejection of this application and other actions according to cooperative policies.
                  </label>
                </div>
              </div>
            </div>

            {/* ── Print Footer ── */}
            <div className="border-t border-slate-200 px-5 sm:px-6 py-2 text-[10px] text-slate-400 flex justify-between">
              <span>Generated by Cooperative Management System</span>
              <span>Page 1 of 1</span>
            </div>
          </div>

          {/* ─── Bottom Action Buttons ─── */}
          <div className="print:hidden flex flex-col sm:flex-row gap-3 mt-6">
            <Button variant="outline" className="sm:flex-1" onClick={() => setStep(2)}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            <Button
              className="sm:flex-1 bg-[#009A44] hover:bg-[#008238] text-white"
              disabled={submitting || !declarationChecked}
              onClick={() => handleSubmit()}
            >
              {submitting ? (
                <>
                  <Loader className="animate-spin mr-2 h-4 w-4" /> Submitting...
                </>
              ) : (
                "Submit Application"
              )}
            </Button>
          </div>
        </div>
      )}

      {/* ═══════════ Step 4: Success ═══════════ */}
      {step === 4 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-8 text-center space-y-4">
            <div className="h-16 w-16 rounded-full bg-green-50 flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-8 w-8 text-[#009A44]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Application Submitted!</h2>
              <p className="text-sm text-slate-500 mt-1">
                Loan application for {selectedMember?.label} has been received successfully.
              </p>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 w-full space-y-2 text-left max-w-sm mx-auto">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Amount</span>
                <span className="font-semibold">{formatCurrency(numericAmount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Duration</span>
                <span className="font-semibold">{numericTerm} months</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Monthly Installment</span>
                <span className="font-semibold">{formatCurrency(Math.round(monthlyInstallment))}</span>
              </div>
            </div>

            <p className="text-xs text-slate-400">
              Application is pending leader approval (3 roles required: President, Secretary, Accountant).
            </p>

            <div className="flex gap-3 pt-2 justify-center">
              <Button variant="outline" className="flex-1 max-w-[200px]" onClick={() => navigate("/loans")}>
                Go to Loans Dashboard
              </Button>
              <Button
                className="flex-1 max-w-[200px] bg-[#009A44] hover:bg-[#008238] text-white"
                onClick={() => {
                  setStep(0);
                  setSelectedMember(null);
                  setAmount("");
                  setPurpose("");
                  setLoanTypeId("0");
                  setTermMonths("12");
                  setDeclarationChecked(false);
                }}
              >
                Apply for Another Member
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
